import { useEffect, useMemo, useRef, useState } from 'react';
import { molarMass } from '../formula';
import {
  VizCard,
  NowYouTry,
  usePrefersReducedMotion,
  useMountSequence,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// MoleMap (type 'mole_map') — the mole "road map": a central MOLES hub wired to
// MASS (÷/× molar mass), PARTICLES (÷/× Avogadro 6.022×10²³ /mol) and gas VOLUME
// (÷/× 22.4 L/mol at STP). Each spoke is labelled with the conversion factor and
// the operation in BOTH directions (toward-moles = divide, away-from-moles =
// multiply). This is the conceptual diagram (not a calculator — see
// MoleConverter for that): one path lights up at a time as the narration walks
// it. config: { highlightPath?, formula?, title? }.
//   • cue.step: 0 = whole map, 1 = mass, 2 = particles, 3 = volume.
//   • cue.highlight: 'mass'|'particles'|'volume'|'moles' (or 'g'/'L'/etc).
//   • cue.progress: scrubs through the three paths (overview → mass → … → vol).
// Resilient: empty config renders the full default map; an unparseable formula
// just omits the molar-mass figure (the map itself never depends on it).

export type MolePath = 'mass' | 'particles' | 'volume';

export interface MoleMapConfig {
  /** Pre-select a spoke to emphasise (the others dim). */
  highlightPath?: MolePath;
  /** Formula whose molar mass annotates the mass spoke, e.g. "H2O", "CO2". */
  formula?: string;
  title?: string;
}

interface Props {
  config?: MoleMapConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const NA_LABEL = '6.022×10²³ /mol'; // Avogadro's number (particles per mole)
const MOLAR_VOLUME = '22.4 L/mol';  // molar volume of an ideal gas at STP

// Geometry of the radial diagram (SVG user units; the <svg> scales to fit).
const VB_W = 560;
const VB_H = 360;
const CX = 280; // centre (MOLES hub)
const CY = 184;
const HUB_R = 58;
const NODE_W = 132;
const NODE_H = 64;

// The three satellite nodes, placed around the hub.
interface Spoke {
  key: MolePath;
  label: string;
  unit: string;
  /** centre of the satellite node */
  x: number;
  y: number;
  /** factor shown on the edge */
  factor: string;
  /** operation applied when going FROM this node TO moles */
  toMoles: string;
  /** operation applied when going FROM moles TO this node */
  fromMoles: string;
  accent: string;
}

const SPOKES: Spoke[] = [
  {
    key: 'mass',
    label: 'MASS',
    unit: 'grams (g)',
    x: 88,
    y: 184,
    factor: 'molar mass (g/mol)',
    toMoles: '÷ molar mass',
    fromMoles: '× molar mass',
    accent: '#E0564B',
  },
  {
    key: 'particles',
    label: 'PARTICLES',
    unit: 'atoms / molecules',
    x: 472,
    y: 96,
    factor: NA_LABEL,
    toMoles: `÷ ${NA_LABEL}`,
    fromMoles: `× ${NA_LABEL}`,
    accent: '#A855C7',
  },
  {
    key: 'volume',
    label: 'VOLUME (gas)',
    unit: 'litres at STP',
    x: 472,
    y: 272,
    factor: MOLAR_VOLUME,
    toMoles: `÷ ${MOLAR_VOLUME}`,
    fromMoles: `× ${MOLAR_VOLUME}`,
    accent: '#5FB860',
  },
];

const ORDER: MolePath[] = ['mass', 'particles', 'volume'];

export default function MoleMap({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  // Molar mass annotation for the mass spoke (optional — omitted if unparseable).
  const formula = config.formula?.trim() || '';
  const mm = useMemo(() => (formula ? molarMass(formula) : 0), [formula]);

  // `active` = which spoke is lit. null ⇒ whole map (overview).
  const [active, setActive] = useState<MolePath | null>(config.highlightPath ?? null);
  // `hub` = is the central MOLES node spotlit (during overview / hover).
  const [hubLit, setHubLit] = useState(false);
  const [showTry, setShowTry] = useState(false);
  const interacted = useRef(false);

  // ── Self-demo: pulse the hub, then walk mass → particles → volume, settle on
  // the overview (or the configured highlightPath). Skipped under reduced motion
  // or when a cue is driving.
  const demo = useMountSequence(
    [
      { at: 280, run: () => setHubLit(true) },
      { at: 760, run: () => { setHubLit(false); setActive('mass'); } },
      { at: 1240, run: () => setActive('particles') },
      { at: 1720, run: () => setActive('volume') },
    ],
    {
      skip: reduced || hasCue,
      onSettle: () => {
        setActive(config.highlightPath ?? null);
        setShowTry(true);
      },
    },
  );

  // ── Cue handling. step: 0 = overview (hub lit), 1..3 = a spoke. highlight: a
  // node name. progress: scrubs overview → mass → particles → volume.
  useEffect(() => {
    if (!cue) return;
    setShowTry(false);
    if (typeof cue.step === 'number') {
      const s = Math.round(cue.step);
      if (s <= 0) { setActive(null); setHubLit(true); }
      else { setActive(ORDER[Math.min(ORDER.length - 1, s - 1)]); setHubLit(false); }
      return;
    }
    const h = cueHighlights(cue);
    if (h.length) {
      if (h.some((t) => t === 'moles' || t === 'mole' || t === 'mol')) {
        setActive(null); setHubLit(true); return;
      }
      const found = matchSpoke(h);
      if (found) { setActive(found); setHubLit(false); return; }
    }
    if (typeof cue.progress === 'number') {
      const p = Math.max(0, Math.min(1, cue.progress));
      // 4 buckets: overview, then each spoke.
      const bucket = Math.min(3, Math.floor(p * 4));
      if (bucket === 0) { setActive(null); setHubLit(true); }
      else { setActive(ORDER[bucket - 1]); setHubLit(false); }
    }
  }, [cue]);

  const pick = (key: MolePath | null) => {
    setActive((cur) => (cur === key ? null : key));
    setHubLit(key === null);
    setShowTry(false);
    if (demo.running) demo.cancel();
    if (!interacted.current) {
      interacted.current = true;
      onComplete?.({ type: 'mole_map', path: key ?? 'moles' });
    }
  };

  const dimmed = (key: MolePath) => active != null && active !== key;

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Stoichiometry · The mole road map"
      title={config.title ?? 'The mole map'}
      subtitle="Everything routes through MOLES — toward the hub you divide, away from it you multiply."
      hint="Click a destination to light its conversion path · click MOLES to see the whole map."
    >
      <div className="relative flex w-full flex-col gap-3">
        <NowYouTry show={showTry && !reduced && !hasCue} />

        <div className="overflow-hidden rounded-md border border-ws-border bg-ws-surface-2">
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            width="100%"
            role="img"
            aria-label="Mole conversion road map: moles at the centre connect to mass, particles, and gas volume."
            className="block h-auto w-full"
          >
            {/* Edges first so nodes sit on top. */}
            {SPOKES.map((sp) => (
              <Edge
                key={sp.key}
                spoke={sp}
                lit={active === sp.key}
                dim={dimmed(sp.key)}
                reduced={reduced}
                mm={sp.key === 'mass' && mm > 0 ? mm : 0}
              />
            ))}

            {/* Central MOLES hub. */}
            <g
              onClick={() => pick(null)}
              className="cursor-pointer"
              role="button"
              aria-label="Moles — the central hub"
            >
              <circle
                cx={CX}
                cy={CY}
                r={HUB_R}
                className="fill-ws-accent"
                style={{
                  filter: hubLit && !reduced ? 'drop-shadow(0 0 10px var(--ws-accent))' : undefined,
                  transition: reduced ? undefined : 'filter 280ms ease-out',
                  opacity: active != null && !hubLit ? 0.82 : 1,
                }}
              />
              <text
                x={CX}
                y={CY - 6}
                textAnchor="middle"
                className="fill-ws-on-accent"
                style={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.5 }}
              >
                MOLES
              </text>
              <text
                x={CX}
                y={CY + 14}
                textAnchor="middle"
                className="fill-ws-on-accent"
                style={{ fontSize: 11, opacity: 0.85 }}
              >
                (mol)
              </text>
            </g>

            {/* Satellite nodes. */}
            {SPOKES.map((sp) => (
              <Node
                key={sp.key}
                spoke={sp}
                lit={active === sp.key}
                dim={dimmed(sp.key)}
                reduced={reduced}
                onPick={() => pick(sp.key)}
              />
            ))}
          </svg>
        </div>

        {/* Caption strip — explains the lit path (or the whole-map rule). */}
        <Caption active={active} mm={mm} formula={formula} />
      </div>
    </VizCard>
  );
}

// ── An edge (curved spoke) between the hub and a satellite, carrying the factor
// label and the divide/multiply direction arrows.
function Edge({
  spoke,
  lit,
  dim,
  reduced,
  mm,
}: {
  spoke: Spoke;
  lit: boolean;
  dim: boolean;
  reduced: boolean;
  mm: number;
}) {
  const toLeft = spoke.x < CX;
  // Anchor on the hub edge, pointing toward the node.
  const dx = spoke.x - CX;
  const dy = spoke.y - CY;
  const len = Math.hypot(dx, dy) || 1;
  const x1 = CX + (dx / len) * HUB_R;
  const y1 = CY + (dy / len) * HUB_R;
  // Anchor on the node's near edge.
  const nodeHalf = NODE_W / 2;
  const x2 = spoke.x + (toLeft ? nodeHalf : -nodeHalf);
  const y2 = spoke.y;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;

  const stroke = lit ? spoke.accent : 'var(--ws-border-strong)';
  const factorLabel = mm > 0 ? `${mm.toFixed(2)} g/mol` : spoke.factor;

  return (
    <g style={{ opacity: dim ? 0.25 : 1, transition: reduced ? undefined : 'opacity 280ms ease-out' }}>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={stroke}
        strokeWidth={lit ? 3 : 2}
        strokeLinecap="round"
        style={{ transition: reduced ? undefined : 'stroke 280ms, stroke-width 280ms' }}
      />

      {/* Direction chips: toward moles = ÷, away from moles = ×. */}
      <DirChip
        x={midX}
        y={midY - 16}
        text={spoke.toMoles}
        sub={`${shortNode(spoke.key)} → mol`}
        lit={lit}
        accent={spoke.accent}
      />
      <DirChip
        x={midX}
        y={midY + 16}
        text={spoke.fromMoles}
        sub={`mol → ${shortNode(spoke.key)}`}
        lit={lit}
        accent={spoke.accent}
      />

      {/* The conversion factor that links the two quantities. */}
      <text
        x={midX}
        y={midY + 40}
        textAnchor="middle"
        className="fill-ws-ink-3"
        style={{ fontSize: 9.5, fontWeight: 600 }}
      >
        {factorLabel}
      </text>
    </g>
  );
}

// A small pill on an edge showing one direction's operation (× or ÷).
function DirChip({
  x,
  y,
  text,
  sub,
  lit,
  accent,
}: {
  x: number;
  y: number;
  text: string;
  sub: string;
  lit: boolean;
  accent: string;
}) {
  const w = Math.max(56, text.length * 6.2 + 14);
  return (
    <g>
      <rect
        x={x - w / 2}
        y={y - 9}
        width={w}
        height={18}
        rx={9}
        fill={lit ? accent : 'var(--ws-surface)'}
        stroke={lit ? accent : 'var(--ws-border)'}
        strokeWidth={1}
      />
      <text
        x={x}
        y={y + 3.5}
        textAnchor="middle"
        style={{ fontSize: 10, fontWeight: 700, fill: lit ? '#fff' : 'var(--ws-ink-2)' }}
      >
        {text}
      </text>
      <title>{sub}</title>
    </g>
  );
}

// A satellite quantity node (MASS / PARTICLES / VOLUME).
function Node({
  spoke,
  lit,
  dim,
  reduced,
  onPick,
}: {
  spoke: Spoke;
  lit: boolean;
  dim: boolean;
  reduced: boolean;
  onPick: () => void;
}) {
  return (
    <g
      onClick={onPick}
      className="cursor-pointer"
      role="button"
      aria-label={`${spoke.label} (${spoke.unit})`}
      style={{ opacity: dim ? 0.32 : 1, transition: reduced ? undefined : 'opacity 280ms ease-out' }}
    >
      <rect
        x={spoke.x - NODE_W / 2}
        y={spoke.y - NODE_H / 2}
        width={NODE_W}
        height={NODE_H}
        rx={12}
        fill={lit ? spoke.accent : 'var(--ws-surface)'}
        stroke={lit ? spoke.accent : 'var(--ws-border-strong)'}
        strokeWidth={2}
        style={{
          filter: lit && !reduced ? `drop-shadow(0 0 8px ${spoke.accent})` : undefined,
          transition: reduced ? undefined : 'fill 280ms ease-out, filter 280ms ease-out',
        }}
      />
      {/* Colour dot key (when not lit, so the spoke colour still reads). */}
      {!lit && (
        <circle cx={spoke.x - NODE_W / 2 + 14} cy={spoke.y - NODE_H / 2 + 14} r={4} fill={spoke.accent} />
      )}
      <text
        x={spoke.x}
        y={spoke.y - 4}
        textAnchor="middle"
        style={{ fontSize: 14, fontWeight: 800, fill: lit ? '#fff' : 'var(--ws-ink)' }}
      >
        {spoke.label}
      </text>
      <text
        x={spoke.x}
        y={spoke.y + 14}
        textAnchor="middle"
        style={{ fontSize: 10, fill: lit ? '#fff' : 'var(--ws-ink-3)', opacity: lit ? 0.92 : 1 }}
      >
        {spoke.unit}
      </text>
    </g>
  );
}

// The explanatory caption under the diagram. Reflects the lit path, or the
// whole-map rule when nothing is selected.
function Caption({
  active,
  mm,
  formula,
}: {
  active: MolePath | null;
  mm: number;
  formula: string;
}) {
  if (active == null) {
    return (
      <p className="rounded-md border border-ws-border bg-ws-surface px-3 py-2 text-center text-xs text-ws-ink-2">
        <span className="font-semibold text-ws-accent">Moles</span> sit at the centre of every
        stoichiometry problem. Convert <span className="font-semibold">toward</span> moles by{' '}
        <span className="font-mono text-ws-ink">dividing</span>, and{' '}
        <span className="font-semibold">away</span> from moles by{' '}
        <span className="font-mono text-ws-ink">multiplying</span>.
      </p>
    );
  }
  const sp = SPOKES.find((s) => s.key === active)!;
  const factor =
    active === 'mass' && mm > 0
      ? `${mm.toFixed(2)} g/mol${formula ? ` (${formula})` : ''}`
      : sp.factor;
  return (
    <p className="rounded-md border px-3 py-2 text-center text-xs text-ws-ink-2"
       style={{ borderColor: sp.accent, background: 'var(--ws-surface)' }}>
      <span className="font-semibold" style={{ color: sp.accent }}>{sp.label}</span>{' '}
      ⇄ moles via <span className="font-mono text-ws-ink">{factor}</span>.{' '}
      <span className="font-mono text-ws-ink">{sp.toMoles}</span> to reach moles,{' '}
      <span className="font-mono text-ws-ink">{sp.fromMoles}</span> to leave them.
    </p>
  );
}

// Short label used inside the direction chips' tooltips.
function shortNode(key: MolePath): string {
  return key === 'mass' ? 'g' : key === 'particles' ? 'particles' : 'L';
}

// Match a list of lowercased highlight tokens to a spoke.
function matchSpoke(tokens: string[]): MolePath | null {
  const has = (...keys: string[]) => tokens.some((t) => keys.includes(t));
  if (has('mass', 'gram', 'grams', 'g', 'weight')) return 'mass';
  if (has('particles', 'particle', 'atoms', 'molecules', 'avogadro', 'representative')) return 'particles';
  if (has('volume', 'gas', 'liter', 'litre', 'liters', 'litres', 'l', 'stp')) return 'volume';
  return null;
}
